"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = Tag;
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _feedbackExpressions = _interopRequireDefault(require("@ux/intents/lib/feedback-expressions"));
var _accessories = require("./accessories");
var _circleFilled = _interopRequireDefault(require("@ux/icon/circle-filled"));
const _excluded = ["children", "className", "design", "emphasis", "highcontrast", "icon", "indicator", "size", "type"];
/* eslint-disable no-process-env, no-console */
const UX_TAG_CLASSNAME = 'ux-tag';

/**
 * Determines the icon based on emphasis
 *
 * @param {String} emphasis - One of several possible emphasis types
 * @returns {Component} - An icon based on given emphasis type
 */
function getEmphasisIcon(emphasis) {
  if (emphasis && _accessories.ACCESSORIES[emphasis]) {
    const Accessory = _accessories.ACCESSORIES[emphasis];
    return /*#__PURE__*/_react.default.createElement(Accessory, {
      className: `${UX_TAG_CLASSNAME}-accessory--${emphasis}`,
      role: "presentation"
    });
  }
  return null;
}

/**
 * Prepares the icon for the tag
 *
 * @param {Object} props - Component props
 * @param {Boolean} [props.emphasis] - Provide emphasis for the component
 * @param {Component} [props.icon] - Adds icon component
 * @param {Boolean} [props.indicator] - Adjusts component to present indicator styles
 * @returns {Component} - <Icon/> component
 */
function Icon(props) {
  const {
    emphasis,
    icon,
    indicator
  } = props || {};

  // If indicator
  if (indicator) {
    return /*#__PURE__*/_react.default.createElement(_circleFilled.default, {
      className: `${UX_TAG_CLASSNAME}-accessory--circle`,
      role: "presentation"
    });
  }

  // If no indicator, provide emphasis icon
  if (typeof icon === 'boolean' && icon) {
    return getEmphasisIcon(emphasis);
  }
  if (icon) {
    return /*#__PURE__*/_react.default.cloneElement(icon, {
      role: 'presentation'
    });
  }
  return null;
}
function warn(props) {
  const {
    type
  } = props;
  if (process.env.NODE_ENV === 'production') return;
  if (type) {
    console.warn(`Warning: The use of 'type' elements within @ux/tag is deprecated.
      Please use the 'emphasis' prop instead.`);
  }
}

/**
 * Create a tag component
 *
 * @param {Object} props - Configuration object
 * @param {String} children - Child elements
 * @param {String} className - Optional class name for placement
 * @param {String} design - Visual appearance
 * @param {String} emphasis - (eg., success, critical, warning)
 * @param {Boolean} highcontrast - Determines if high contrast intents should be used
 * @param {Boolean|Node} icon - Determines icon type or allows for custom icon
 * @param {Boolean} indicator - Adjusts component to present indicator styles
 * @param {String} size - sm, md, lg
 * @param {String} type - Alias for emphasis
 * @returns {Component} - <Tag/>
 */
function Tag(_ref) {
  let {
      children,
      className,
      design = 'filled',
      emphasis,
      highcontrast,
      icon,
      indicator = false,
      size = 'md',
      type
    } = _ref,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  delete rest.style;
  warn({
    emphasis,
    icon,
    indicator
  });
  const classNames = (0, _classnames.default)(UX_TAG_CLASSNAME, `${UX_TAG_CLASSNAME}-${emphasis || type}`, className, {
    [`${UX_TAG_CLASSNAME}--size-${size}`]: ['sm', 'md', 'lg'].includes(size),
    [`${UX_TAG_CLASSNAME}--design-${design}`]: ['filled', 'inline'].includes(design),
    [`${UX_TAG_CLASSNAME}--indicator`]: indicator,
    [`${UX_TAG_CLASSNAME}--highcontrast`]: highcontrast || indicator || ['inline'].includes(design)
  });
  return /*#__PURE__*/_react.default.createElement("span", {
    "data-version": "2400",
    className: classNames
  }, /*#__PURE__*/_react.default.createElement(Icon, {
    icon: icon,
    emphasis: emphasis || type,
    indicator: indicator
  }), children || emphasis || type);
}
Tag.ACCESSORIES = _accessories.ACCESSORIES;
Tag.propTypes = {
  /** Tag content */
  children: _propTypes.default.string,
  /** Optional class name for placement */
  className: _propTypes.default.string,
  /** Provides additional design information */
  design: _propTypes.default.oneOf(['filled',
  // Default
  'inline' // Removes box styles
  ]),

  /** The type of tag, informs colors */
  emphasis: _propTypes.default.oneOf(Object.values(_feedbackExpressions.default).map(v => v.toLowerCase())),
  /** Sets usage of feedback colors to the high contrast versions */
  highcontrast: _propTypes.default.bool,
  /** Adds icon to the component */
  icon: _propTypes.default.node,
  /** Adjusts component to present indicator styles */
  indicator: _propTypes.default.bool,
  /** Determines the size */
  size: _propTypes.default.oneOf(['sm', 'md', 'lg']),
  /** Legacy alias for emphasis */
  type: _propTypes.default.oneOf(Object.values(_feedbackExpressions.default).map(v => v.toLowerCase()))
};